// src/components/job-engine/useJobEngineSignalR.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { EventType } from "@azure/msal-browser";
import type { EventMessage } from "@azure/msal-browser";
import { acquireToken } from "@/authentication/TokenService";
import { msalConfig } from "@/authentication/authConfig";
import { signalRService } from "./signalRService";

interface UseJobEngineSignalROptions {
  apiBaseUrl: string;
  customerId: string;
  enabled?: boolean;
}

interface UseJobEngineSignalRResult {
  isConnected: boolean;
  connectionId: string | null;
}

const RECONNECT_MS = 10_000;
const HEALTH_CHECK_MS = 30_000;

export default function useJobEngineSignalR({
  apiBaseUrl,
  customerId,
  enabled = true,
}: UseJobEngineSignalROptions): UseJobEngineSignalRResult {
  const { instance } = useMsal();
  const isConnectingRef = useRef(false);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const [connectedTick, setConnectedTick] = useState(0);

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      return await acquireToken(false);
    } catch {
      try {
        const r = await instance.acquireTokenPopup({ scopes: [msalConfig.scope] });
        return r.accessToken;
      } catch {
        return null;
      }
    }
  }, [instance]);

  const start = useCallback(async () => {
    if (!enabled || isConnectingRef.current || signalRService.isConnected()) return;
    isConnectingRef.current = true;
    try {
      const token = await getToken();
      if (!token) {
        isConnectingRef.current = false;
        return;
      }
      await signalRService.startConnection(apiBaseUrl, token, customerId);
      setConnectedTick((n) => n + 1);
    } catch {
      if (reconnectTimeoutRef.current) window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = window.setTimeout(() => {
        start().catch(() => {});
      }, RECONNECT_MS);
    } finally {
      isConnectingRef.current = false;
    }
  }, [enabled, apiBaseUrl, customerId, getToken]);

  const stop = useCallback(async () => {
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    await signalRService.stopConnection();
    setConnectedTick((n) => n + 1);
  }, []);

  // Mount: start. Unmount: stop.
  useEffect(() => {
    if (enabled) start().catch(() => {});
    return () => {
      stop().catch(() => {});
    };
  }, [enabled, start, stop]);

  // Health check every 30s: reconnect if dropped.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (enabled && !signalRService.isConnected()) start().catch(() => {});
    }, HEALTH_CHECK_MS);
    return () => window.clearInterval(id);
  }, [enabled, start]);

  // React to MSAL auth events that may require a reconnect.
  useEffect(() => {
    const id = instance.addEventCallback((message: EventMessage) => {
      switch (message.eventType) {
        case EventType.LOGIN_SUCCESS:
        case EventType.ACQUIRE_TOKEN_SUCCESS:
        case EventType.HANDLE_REDIRECT_END:
          if (enabled && !signalRService.isConnected()) start().catch(() => {});
          break;
        default:
          break;
      }
    });
    return () => {
      if (id) instance.removeEventCallback(id);
    };
  }, [instance, enabled, start]);

  // Re-render consumers when connection state flips
  void connectedTick;
  return {
    isConnected: signalRService.isConnected(),
    connectionId: signalRService.getConnectionId(),
  };
}
